// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { AdditionalAvailabilityInfoModel } from "@guestsupply/Overrides/Components/ProductAvailability";
import { addToCartStatus } from "@guestsupply/XDL/constants";
import Cart from "@guestsupply/XDL/Icons/Cart";
import CartFilled from "@guestsupply/XDL/Icons/CartFilled";
import Check from "@guestsupply/XDL/Icons/Check";
import { color, mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // XNG-Changes
    iconOnly?: boolean;
    showDisabled?: boolean;
    showIconWithLabel?: boolean;
    //  XNG-CHANGES : To dicard accounting the price when checking disabled (for autocomplete and featured products)
    discardPrice?: boolean;
    isDisabled?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    HasHistory;

// XNG-Changes
export interface IconConfig {
    src: React.ComponentType | string;
    position: "right" | "left";
}

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    setNavDrawerIsOpen,
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    typographyProps: {
        size: 12,
    },
};

const iconButtonStyles = {
    css: css`
        padding: 0 16px;
        width: 54px;
        min-width: 54px;
        justify-content: center;
        &.loading,
        &.success {
            padding: 0 16px;
        }
        ${ButtonIcon} {
            margin: 0;
        }
    `,
};

const ProductAddToCartButton = ({
    productContext: {
        product,
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    isTabletScreen,
    setNavDrawerIsOpen,
    iconOnly,
    signInUrl,
    currentLocation,
    showDisabled,
    showIconWithLabel = false,
    discardPrice = false,
    isDisabled,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeWithCss(productAddToCartButtonStyles, extendedStyles));
    const [status, setStatus] = useState(addToCartStatus.default);
    const history = useHistory();

    const { id: productId, allowZeroPricing, quoteRequired } = product;
    // XNG-CHANGES : checking canaddtocart from additionalAvailabilityInfo messagetype (disabled if messagetype =401)
    const additionalAvailabilityInfo =
        inventory?.additionalResults?.additionalAvailabilityInfo &&
        (JSON.parse(inventory?.additionalResults?.additionalAvailabilityInfo) as AdditionalAvailabilityInfoModel);

    // XNG-Changes
    const iconConfig: IconConfig = {
        src: status === addToCartStatus.success ? Check : CartFilled,
        position: "left",
        ...{ size: 16 },
    };

    // XNG-Changes
    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    const redirectToSignIn = () => {
        if (!signInUrl) {
            logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
    };

    // XNG-Changes: PPSC-482 && PPSC-516
    const disabled =
        isDisabled ||
        (!hasEnoughInventory && !failedToLoadInventory) ||
        !canAddToCart ||
        // XNG-CHANGES : To dicard accounting the price when checking disabled
        (!pricing && !discardPrice) ||
        (!allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) ||
        (!!additionalAvailabilityInfo &&
            (Number(additionalAvailabilityInfo.messageType) === 401 ||
                (Number(additionalAvailabilityInfo.messageType) === 2 && !(product as any)?.canBackOrder)));

    if (disabled && !showDisabled && pricing) {
        return null;
    }

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        // XNG-Changes
        setStatus(addToCartStatus.loading);

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        // XNG-Changes
        setStatus(addToCartStatus.success);

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                // XNG-CHANGES -GSS-655 :remove ootb timeoutLength when isQtyAdjusted
                timeoutLength: addToCartPopupTimeout,
            });
        }

        if (isTabletScreen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    };

    /**
     * XNG-Changes
     * to prevent button width change when the status change, we use position absolute and text-color: transparent
     */
    if (iconOnly) {
        return (
            <Button
                onClick={addToCartClickHandler}
                className={`button-wrapper-icon-only ${status.status}`}
                disabled={
                    disabled || qtyOrdered <= 0 || isAddToCartButtonDisabled || status !== addToCartStatus.default
                }
                {...iconButtonStyles}
                {...otherProps}
                aria-label="add to cart icon button"
                icon={iconConfig}
                {...{ isLoading: status === addToCartStatus.loading }}
            />
        );
    }

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            // XNg-Changes: add status !== addToCartStatus.default
            disabled={disabled || qtyOrdered <= 0 || isAddToCartButtonDisabled || status !== addToCartStatus.default}
            className={`${status.status}`}
            icon={showIconWithLabel ? iconConfig : undefined}
            {...otherProps}
            aria-label="add to cart button"
            {...{ isLoading: status === addToCartStatus.loading }}
        >
            <Typography {...productAddToCartButtonStyles.typographyProps}>
                {labelOverride ?? translate(addToCartStatus.default.label || "")}
            </Typography>
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductAddToCartButton)));
